import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import confetti from "canvas-confetti"
import { Coins, Gift, Loader2, Package, RefreshCcw, Sparkles, Swords, Target, X } from "lucide-react"
import { useAuth, type DealSlotReward } from "@/contexts/AuthContext"
import { useSound } from "@/hooks/useSound"
import { BonusRoundWheel } from "@/components/dashboard/BonusRoundWheel"
import { BonusSuperSlots } from "@/components/dashboard/BonusSuperSlots"
import { SlotIcon } from "@/components/dashboard/SlotIcons"
import leverPullSfx from "@/public/sounds/lever-pull.wav"
import reelStopSfx from "@/public/sounds/reel-stop.wav"
import winJingleSfx from "@/public/sounds/win-jingle.wav"
import spinWhooshSfx from "@/public/sounds/spin-whoosh.wav"
import wheelTickSfx from "@/public/sounds/wheel-tick.wav"
import bonusRevealSfx from "@/public/sounds/bonus-reveal.wav"
import lossBuzzerSfx from "@/public/sounds/loss-buzzer.mp3"
import sparkleLottie from "@/public/lotties/sparkle-overlay.lottie"
import confettiLottie from "@/public/lotties/confetti-celebration.lottie"

type SlotPhase = "ready" | "claiming" | "spinning" | "suspense" | "reveal" | "done" | "miss" | "error"
type BonusWheelPhase = "hidden" | "ready" | "spinning" | "done"

// ── Timing (casino-researched) ─────────────────────────────────────────
// Reel stops use INCREASING gaps: 600 → 900 → 1200ms (last reel = most dramatic)
const BASE_REEL_SPIN_MS = 2800
const REEL_STAGGER = [0, 1400, 3400] as const // cumulative offsets per reel — big gaps for suspense
const LAST_REEL_STOP = BASE_REEL_SPIN_MS + REEL_STAGGER[2]
const SUSPENSE_PAUSE_MS = 2000 // strategic silence after last reel stops
const REVEAL_MS = LAST_REEL_STOP + SUSPENSE_PAUSE_MS
const TOTAL_SPIN_MS = REVEAL_MS + 200

const SPIN_SYMBOLS = ["cherry", "lemon", "watermelon", "bell", "seven", "diamond", "coin", "bill", "gift", "star", "refresh", "swords"] as const

interface DealRewardSlotStepProps {
  dealId?: string
  memberId?: string
  recipientLabel?: string
  onSubmitAnother: () => void
  onClose: () => void
  onBusyChange?: (busy: boolean) => void
  onRewardSettled?: (reward: DealSlotReward) => void
  initialFreeSpins?: number
  freeSpinOnly?: boolean
}

function getMainBonusCashAmount(reward: DealSlotReward | null): number {
  if (!reward || reward.type !== "bonus") return 0
  if (typeof reward.mainGameAmount === "number" && Number.isFinite(reward.mainGameAmount)) {
    return Math.max(0, reward.mainGameAmount)
  }
  return Math.max(0, reward.amount)
}

function getBonusRoundCashAmount(reward: DealSlotReward | null): number {
  if (!reward || reward.type !== "bonus") return 0
  if (typeof reward.bonusGameAmount === "number" && Number.isFinite(reward.bonusGameAmount)) {
    return Math.max(0, reward.bonusGameAmount)
  }
  return 0
}

function getTotalBonusCashAmount(reward: DealSlotReward | null): number {
  if (!reward || reward.type !== "bonus") return 0
  if (typeof reward.totalCashAmount === "number" && Number.isFinite(reward.totalCashAmount)) {
    return Math.max(0, reward.totalCashAmount)
  }
  const main = getMainBonusCashAmount(reward)
  const bonus = getBonusRoundCashAmount(reward)
  return bonus > 0 ? main + bonus : main
}

function rewardToReels(reward: DealSlotReward): [string, string, string] {
  if (reward.type === "points" || reward.type === "bonus") return ["coin", "coin", "coin"]
  if (reward.type === "item") return ["cherry", "cherry", "cherry"]
  if (reward.type === "free_spins") return ["refresh", "refresh", "refresh"]
  if (reward.type === "side_quest") return ["swords", "swords", "swords"]
  return ["middleFinger", "middleFinger", "middleFinger"] // loss
}

// ── Multi-spin hit/miss system ─────────────────────────────────────────
// Escalating hit probability per attempt (1-indexed). 5th attempt = guaranteed outcome.
const HIT_CHANCE_BY_ATTEMPT = [0, 0.20, 0.35, 0.50, 0.70, 1.0] as const
const MAX_SPIN_ATTEMPTS = 5

function getHintSymbol(reward: DealSlotReward): string {
  if (reward.type === "points" || reward.type === "bonus") return "coin"
  if (reward.type === "item") return "cherry"
  if (reward.type === "free_spins") return "refresh"
  if (reward.type === "side_quest") return "swords"
  return "middleFinger"
}

/** Generate reels guaranteed NOT to be 3-in-a-row. nearMiss = show 2 matching + 1 diff for tension. */
function generateMissReels(nearMiss: boolean, hintSymbol?: string): [string, string, string] {
  if (nearMiss && hintSymbol) {
    const oddPos = Math.floor(Math.random() * 3)
    const others = SPIN_SYMBOLS.filter(s => s !== hintSymbol)
    const diff = others[Math.floor(Math.random() * others.length)]!
    return [0, 1, 2].map(i => i === oddPos ? diff : hintSymbol) as [string, string, string]
  }
  const a = SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!
  const b = SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!
  let c: string
  if (a === b) {
    const others = SPIN_SYMBOLS.filter(s => s !== a)
    c = others[Math.floor(Math.random() * others.length)]!
  } else {
    c = SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!
  }
  return [a, b, c]
}

function rewardAccent(reward: DealSlotReward | null) {
  if (!reward) return "#FFD700"
  if (reward.type === "points") return "#32CD32"
  if (reward.type === "bonus") return "#00CED1"
  if (reward.type === "item") return "#F37020"
  if (reward.type === "free_spins") return "#9B59B6"
  if (reward.type === "side_quest") return "#FF6B9D"
  return "#6A6A8A"
}

function rewardTitle(reward: DealSlotReward | null, opts?: { hideBonusAmount?: boolean; bonusCashAmount?: number }) {
  if (!reward) return "Spin For a Reward"
  if (reward.type === "points") return `You won ${reward.amount} points`
  if (reward.type === "bonus") {
    if (opts?.hideBonusAmount) return "Bonus Round Unlocked!"
    const cashAmount = typeof opts?.bonusCashAmount === "number" ? opts.bonusCashAmount : reward.amount
    return `You won $${cashAmount} bonus`
  }
  if (reward.type === "item") return `You won ${reward.label || "an Oval item"}`
  if (reward.type === "free_spins") return `You won ${reward.amount} free ${reward.amount === 1 ? "spin" : "spins"}`
  if (reward.type === "side_quest") return "Side Quest Unlocked!"
  return "No reward this spin"
}

function rewardSubtitle(
  reward: DealSlotReward | null,
  recipientLabel?: string,
  opts?: { hideBonusAmount?: boolean; freeSpinOnly?: boolean },
) {
  const recipient = recipientLabel || "you"
  if (!reward) {
    return opts?.freeSpinOnly
      ? "Match 3 in a row to win! Keep spinning until you hit — guaranteed by the 5th try."
      : "Match 3 in a row to win! Keep spinning until you hit — guaranteed by the 5th try."
  }
  if (reward.type === "points") return `Added to ${recipient}'s points balance`
  if (reward.type === "bonus") {
    if (opts?.hideBonusAmount) return "Spin the Oval Bonus Wheel to reveal your cash amount"
    return `Added to ${recipient}'s pending bonus balance`
  }
  if (reward.type === "item") return "Physical prize recorded for fulfillment (Oval merch drop)"
  if (reward.type === "free_spins") return `Use your free ${reward.amount === 1 ? "spin" : "spins"} below for another chance at rewards!`
  if (reward.type === "side_quest" && reward.quest) {
    const days = reward.quest.deadlineHours >= 24 ? `${Math.round(reward.quest.deadlineHours / 24)} day${Math.round(reward.quest.deadlineHours / 24) !== 1 ? "s" : ""}` : `${reward.quest.deadlineHours}h`
    return `Submit ${reward.quest.target} deal${reward.quest.target !== 1 ? "s" : ""} in ${days} → $${reward.quest.cashPrize} cash prize`
  }
  if (reward.type === "side_quest") return "A timed challenge has been assigned — check the sidebar!"
  return "No bonus this time, but your deal was submitted successfully."
}

/* ── Count-up animation hook ─────────────────────────────────────────── */
function useCountUp(target: number, active: boolean, durationMs = 1200) {
  const [value, setValue] = React.useState(0)
  React.useEffect(() => {
    if (!active || target <= 0) { setValue(target); return }
    setValue(0)
    const start = performance.now()
    let raf: number
    function tick(now: number) {
      const t = Math.min((now - start) / durationMs, 1)
      // ease-out cubic — slow at end for dramatic landing
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(Math.round(eased * target))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, active, durationMs])
  return value
}

/* ── Slot reel ────────────────────────────────────────────────────────── */

function EmojiReel({
  spinning,
  finalSymbol,
  delayMs,
  onStop,
  spotlight,
}: {
  spinning: boolean
  finalSymbol: string
  delayMs: number
  onStop?: () => void
  spotlight?: boolean // true = this reel is highlighted, others are dimmed
}) {
  const [display, setDisplay] = React.useState<string>(SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!)
  const [isSpinning, setIsSpinning] = React.useState(false)
  const [justLanded, setJustLanded] = React.useState(false)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const onStopRef = React.useRef(onStop)
  onStopRef.current = onStop

  React.useEffect(() => {
    if (!spinning) {
      // Ensure clean state when spinning prop goes false
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      setIsSpinning(false)
      return
    }

    setIsSpinning(true)
    setJustLanded(false)
    intervalRef.current = setInterval(() => {
      setDisplay(SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!)
    }, 75)

    const stopTimer = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      setDisplay(finalSymbol)
      setIsSpinning(false)
      setJustLanded(true)
      onStopRef.current?.()
      // Reset the "just landed" flash after the bounce
      setTimeout(() => setJustLanded(false), 500)
    }, BASE_REEL_SPIN_MS + delayMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      clearTimeout(stopTimer)
    }
  }, [spinning, finalSymbol, delayMs])

  return (
    <div
      className="relative flex-1 min-w-0 rounded-xl p-[2px] overflow-hidden transition-opacity duration-300"
      style={{
        background: justLanded
          ? "linear-gradient(180deg, #FFD700, #F37020, #FFD700)"
          : isSpinning
            ? "linear-gradient(180deg, #6A6A8A, #FFD700, #3A3A55)"
            : "linear-gradient(180deg, #3A3A55, #2A2A45, #1A1A30)",
        opacity: spotlight === false ? 0.35 : 1,
      }}
    >
      <div className="relative rounded-[10px] h-[118px] overflow-hidden bg-gradient-to-b from-[#161630] to-[#0A0A18]">
        {/* Landing flash */}
        <AnimatePresence>
          {justLanded && (
            <motion.div
              className="absolute inset-0 z-20 pointer-events-none bg-white rounded-[10px]"
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>

        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),transparent_20%,transparent_80%,rgba(0,0,0,0.3))]" />
        <div className="absolute inset-x-3 top-2 h-3 rounded-full bg-white/[0.03] blur-sm" />

        <motion.div
          className="relative h-full flex items-center justify-center text-5xl sm:text-6xl"
          animate={
            isSpinning
              ? { y: [0, -5, 4, -3, 0], scale: [1, 1.04, 0.97, 1.03, 1] }
              : justLanded
                // Overshoot-bounce: spring past target then settle (casino reel feel)
                ? { y: [12, -8, 4, -2, 0], scale: [0.92, 1.12, 0.96, 1.04, 1] }
                : { y: 0, scale: 1 }
          }
          transition={
            isSpinning
              ? { duration: 0.14, repeat: Infinity, ease: "linear" }
              : justLanded
                ? { duration: 0.45, ease: [0.22, 1.2, 0.36, 1], times: [0, 0.3, 0.55, 0.75, 1] }
                : { duration: 0.01 }
          }
        >
          <span
            aria-hidden
            style={{
              filter: isSpinning
                ? "blur(1.8px) saturate(1.3) brightness(1.1)"
                : "drop-shadow(0 6px 16px rgba(0,0,0,0.5)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              transition: "filter 0.05s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SlotIcon symbol={display} size={52} />
          </span>
        </motion.div>

        <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-[#0A0A18]/90 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#0A0A18]/90 to-transparent pointer-events-none" />
        <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-[48px] border-y border-[#FFD700]/10 pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.04)_0%,transparent_50%)] pointer-events-none" />
      </div>
    </div>
  )
}

/* ── Screen shake wrapper ─────────────────────────────────────────────── */

function ShakeContainer({ shake, children }: { shake: boolean; children: React.ReactNode }) {
  return (
    <motion.div
      animate={
        shake
          ? { x: [0, -4, 5, -3, 4, -2, 0], y: [0, 3, -4, 2, -3, 1, 0] }
          : { x: 0, y: 0 }
      }
      transition={shake ? { duration: 0.35, ease: "easeOut" } : { duration: 0.1 }}
    >
      {children}
    </motion.div>
  )
}

/* ── Main component ───────────────────────────────────────────────────── */

export function DealRewardSlotStep({
  dealId,
  memberId,
  recipientLabel,
  onSubmitAnother,
  onClose,
  onBusyChange,
  onRewardSettled,
  initialFreeSpins = 0,
  freeSpinOnly = false,
}: DealRewardSlotStepProps) {
  const { claimDealSlotReward, claimFreeSpinReward } = useAuth()
  const [phase, setPhase] = React.useState<SlotPhase>("ready")
  const [reward, setReward] = React.useState<DealSlotReward | null>(null)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [leverPulled, setLeverPulled] = React.useState(false)
  const [bonusWheelPhase, setBonusWheelPhase] = React.useState<BonusWheelPhase>("hidden")
  const [bonusMode, setBonusMode] = React.useState<"wheel" | "super_slots">("wheel")
  const [showWinGlow, setShowWinGlow] = React.useState(false)
  const [reelsLanded, setReelsLanded] = React.useState(0) // 0, 1, 2, 3
  const [shaking, setShaking] = React.useState(false)
  const [pendingFreeSpins, setPendingFreeSpins] = React.useState(Math.max(0, initialFreeSpins))
  const [isFreeSpin, setIsFreeSpin] = React.useState(false)
  const [spinAttempt, setSpinAttempt] = React.useState(0) // 0 = hasn't spun, 1-5 = current attempt
  const [pendingReward, setPendingReward] = React.useState<DealSlotReward | null>(null) // cached reward from API
  const [currentReels, setCurrentReels] = React.useState<[string, string, string]>(["cherry", "bell", "seven"])
  const settleTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const timersRef = React.useRef<ReturnType<typeof setTimeout>[]>([])

  // Sounds
  const { play: playLeverPull } = useSound(leverPullSfx, { volume: 0.6 })
  const { play: playReelStop } = useSound(reelStopSfx, { volume: 0.55 })
  const { play: playWinJingle } = useSound(winJingleSfx, { volume: 0.7 })
  const { play: playWhoosh } = useSound(spinWhooshSfx, { volume: 0.5 })
  const { play: playTick } = useSound(wheelTickSfx, { volume: 0.15 })
  const { play: playReveal } = useSound(bonusRevealSfx, { volume: 0.55 })
  const { play: playLoss } = useSound(lossBuzzerSfx, { volume: 0.5 })

  const isBusy = phase === "claiming" || phase === "spinning" || phase === "suspense" || phase === "reveal" || bonusWheelPhase === "spinning"
  const reelValues = currentReels
  const isBonusAwaitingWheel = reward?.type === "bonus" && bonusWheelPhase !== "done"
  const bonusRoundRevealAmount = reward?.type === "bonus"
    ? Math.max(0, getBonusRoundCashAmount(reward) || reward.amount)
    : 0
  const visibleBonusCashAmount = reward?.type === "bonus"
    ? (bonusWheelPhase === "done" ? getTotalBonusCashAmount(reward) : getMainBonusCashAmount(reward))
    : 0
  const accent = rewardAccent(reward)
  const isWin = reward != null && reward.type !== "nothing"
  const displayMemberId = memberId || "FREE SPIN"

  // Count-up for reward amount display
  const countUpActive = phase === "done" && isWin
  const countTarget = reward?.type === "points" ? reward.amount : reward?.type === "bonus" ? visibleBonusCashAmount : 0
  const countedValue = useCountUp(countTarget, countUpActive)

  React.useEffect(() => {
    onBusyChange?.(isBusy)
  }, [isBusy, onBusyChange])

  React.useEffect(() => {
    return () => {
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current)
      if (tickTimerRef.current) clearTimeout(tickTimerRef.current)
      timersRef.current.forEach(clearTimeout)
      onBusyChange?.(false)
    }
  }, [onBusyChange])

  // Decelerating tick sounds — ascending pitch feel from interval compression
  const startSpinTicks = React.useCallback(() => {
    let interval = 80
    const multiplier = 1.07
    const spinEnd = Date.now() + LAST_REEL_STOP - 200

    function scheduleTick() {
      if (Date.now() >= spinEnd) return
      playTick()
      interval = Math.min(interval * multiplier, 500)
      tickTimerRef.current = setTimeout(scheduleTick, interval)
    }
    scheduleTick()
  }, [playTick])

  // Screen-shake trigger
  const triggerShake = React.useCallback(() => {
    setShaking(true)
    const t = setTimeout(() => setShaking(false), 350)
    timersRef.current.push(t)
  }, [])

  // Reel stop handler — tracks how many reels have landed
  const handleReelStop = React.useCallback((reelIndex: number) => {
    playReelStop()
    triggerShake()
    setReelsLanded((prev) => Math.max(prev, reelIndex + 1))
  }, [playReelStop, triggerShake])

  // Miss animation — reels spin and land on non-matching symbols (no celebration)
  const runMissAnimation = React.useCallback((missReelValues: [string, string, string]) => {
    setCurrentReels(missReelValues)
    setPhase("spinning")

    playWhoosh()
    startSpinTicks()

    // After all reels stop → brief pause → show miss
    const missTimer = setTimeout(() => {
      setPhase("miss")
      playLoss()
      triggerShake()
    }, LAST_REEL_STOP + 400)
    timersRef.current.push(missTimer)
  }, [playWhoosh, startSpinTicks, playLoss, triggerShake])

  // Hit animation — kicks off once reward is resolved and reels match (3-in-a-row)
  const runSpinAnimation = React.useCallback((resolvedReward: DealSlotReward) => {
    const resolvedIsWin = resolvedReward.type !== "nothing"
    setReward(resolvedReward)
    setCurrentReels(rewardToReels(resolvedReward))
    setPhase("spinning")

    // Track free spins won
    if (resolvedReward.type === "free_spins" && resolvedReward.amount > 0) {
      setPendingFreeSpins((prev) => prev + resolvedReward.amount)
    }

    // Sounds: whoosh + decelerating ticks
    playWhoosh()
    startSpinTicks()

    // Phase: SUSPENSE — strategic silence after last reel stops
    const suspenseTimer = setTimeout(() => {
      setPhase("suspense")
    }, LAST_REEL_STOP + 300)
    timersRef.current.push(suspenseTimer)

    // Phase: REVEAL — the climax
    const revealTimer = setTimeout(() => {
      setPhase("reveal")
      setShowWinGlow(true)

      if (resolvedReward.type === "nothing") {
        // Loss: play sad buzzer
        playLoss()
      } else {
        // Wave 1: Screen flash + sound
        playReveal()
        triggerShake()

        // Wave 2: Confetti burst (200ms after reveal)
        const confettiTimer = setTimeout(() => {
          playWinJingle()
          confetti({
            particleCount: resolvedReward.type === "bonus" || resolvedReward.type === "item" ? 140 : 90,
            spread: 80,
            origin: { y: 0.5 },
            colors: ["#FFD700", "#F37020", "#32CD32", "#00CED1"],
          })
        }, 200)
        timersRef.current.push(confettiTimer)

        // Wave 3: Second confetti rain (1.2s)
        const rain = setTimeout(() => {
          confetti({
            particleCount: 50,
            spread: 120,
            origin: { y: 0.3, x: 0.3 },
            colors: ["#FFD700", "#F37020"],
          })
          confetti({
            particleCount: 50,
            spread: 120,
            origin: { y: 0.3, x: 0.7 },
            colors: ["#32CD32", "#00CED1"],
          })
        }, 1400)
        timersRef.current.push(rain)
      }

      // Phase: DONE — after celebration waves
      const doneTimer = setTimeout(() => {
        setPhase("done")
        if (resolvedReward.type === "bonus") {
          setBonusMode(Math.random() < 0.5 ? "wheel" : "super_slots")
          setBonusWheelPhase("ready")
        } else {
          onRewardSettled?.(resolvedReward)
        }
      }, resolvedIsWin ? 2200 : 800)
      timersRef.current.push(doneTimer)
    }, REVEAL_MS)
    timersRef.current.push(revealTimer)
  }, [playWhoosh, startSpinTicks, playLoss, playReveal, triggerShake, playWinJingle, onRewardSettled])

  // Determine hit vs miss for a given attempt, then kick off the right animation
  const resolveSpinAttempt = React.useCallback((attempt: number, storedReward: DealSlotReward) => {
    const hitChance = attempt >= MAX_SPIN_ATTEMPTS ? 1 : (HIT_CHANCE_BY_ATTEMPT[attempt] ?? 1)
    const isHit = Math.random() < hitChance

    if (isHit) {
      runSpinAnimation(storedReward)
    } else {
      const nearMiss = attempt >= 3 && Math.random() < (attempt === 3 ? 0.5 : 0.7)
      const missReelValues = generateMissReels(nearMiss, getHintSymbol(storedReward))
      runMissAnimation(missReelValues)
    }
  }, [runSpinAnimation, runMissAnimation])

  const handleSpin = async () => {
    if (isBusy) return
    if (!dealId) {
      setPhase("error")
      setErrorMessage("No deal is available for this spin")
      return
    }

    setLeverPulled(true)
    playLeverPull()
    setTimeout(() => setLeverPulled(false), 380)
    setErrorMessage(null)
    setBonusWheelPhase("hidden")
    setShowWinGlow(false)
    setReelsLanded(0)
    setIsFreeSpin(false)
    setSpinAttempt(0)
    setPendingReward(null)
    setPhase("claiming")

    const result = await claimDealSlotReward(dealId)
    if (!result.success || !result.reward) {
      setPhase("error")
      setErrorMessage(result.message || "Unable to spin right now")
      return
    }

    const resolvedReward = result.reward
    setPendingReward(resolvedReward)
    const attempt = 1
    setSpinAttempt(attempt)
    resolveSpinAttempt(attempt, resolvedReward)
  }

  const handleFreeSpin = async () => {
    if (isBusy || pendingFreeSpins <= 0) return

    setPendingFreeSpins((prev) => prev - 1)
    setLeverPulled(true)
    playLeverPull()
    setTimeout(() => setLeverPulled(false), 380)
    setErrorMessage(null)
    setBonusWheelPhase("hidden")
    setShowWinGlow(false)
    setReelsLanded(0)
    setIsFreeSpin(true)
    setSpinAttempt(0)
    setPendingReward(null)
    setPhase("claiming")

    const result = await claimFreeSpinReward()
    if (!result.success || !result.reward) {
      setPhase("error")
      setErrorMessage(result.message || "Unable to use free spin right now")
      setPendingFreeSpins((prev) => prev + 1) // refund the spin on error
      return
    }

    const resolvedReward = result.reward
    setPendingReward(resolvedReward)
    const attempt = 1
    setSpinAttempt(attempt)
    resolveSpinAttempt(attempt, resolvedReward)
  }

  // Spin Again after a miss — no API call, just re-roll hit/miss from cached reward
  const handleSpinAgain = React.useCallback(() => {
    if (!pendingReward) return

    setLeverPulled(true)
    playLeverPull()
    setTimeout(() => setLeverPulled(false), 380)
    setShowWinGlow(false)
    setReelsLanded(0)

    const attempt = spinAttempt + 1
    setSpinAttempt(attempt)
    resolveSpinAttempt(attempt, pendingReward)
  }, [pendingReward, spinAttempt, playLeverPull, resolveSpinAttempt])

  const handleBonusWheelComplete = React.useCallback(() => {
    if (!reward || reward.type !== "bonus") return
    setBonusWheelPhase("done")
    const totalBonusCashAmount = getTotalBonusCashAmount(reward)
    onRewardSettled?.({
      ...reward,
      amount: totalBonusCashAmount,
      totalCashAmount: totalBonusCashAmount,
    })
  }, [onRewardSettled, reward])

  // Spotlight: all reels stay fully visible (no dimming)
  const spotlightReel = (_index: number) => undefined

  // Background brightness escalation
  const bgIntensity =
    phase === "spinning" ? 0.3 + (reelsLanded * 0.1) :
    phase === "suspense" ? 0.65 :
    phase === "reveal" ? 0.8 :
    phase === "miss" ? 0.15 :
    0.2

  return (
    <div className="space-y-4">
      <ShakeContainer shake={shaking}>
        <div className="relative rounded-2xl border-2 border-[#2A2A45] bg-[linear-gradient(180deg,#191930,#121220)] p-4 overflow-hidden">
          {/* Ambient background glows — intensity escalates with phase */}
          <motion.div
            className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,#FFD700,transparent_35%),radial-gradient(circle_at_80%_10%,#00CED1,transparent_40%),radial-gradient(circle_at_50%_100%,#32CD32,transparent_45%)]"
            animate={{ opacity: bgIntensity }}
            transition={{ duration: 0.6 }}
          />

          {/* Reveal flash — full white overlay 60% for 150ms */}
          <AnimatePresence>
            {phase === "reveal" && isWin && (
              <motion.div
                className="absolute inset-0 z-40 pointer-events-none bg-white rounded-2xl"
                initial={{ opacity: 0.55 }}
                animate={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </AnimatePresence>

          {/* Sparkle overlay during spin */}
          {(phase === "spinning" || phase === "suspense") && (
            <div className="absolute inset-0 z-30 pointer-events-none opacity-60">
              <DotLottieReact src={sparkleLottie} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}

          {/* Confetti Lottie overlay on reveal */}
          {(phase === "reveal" || phase === "done") && isWin && (
            <div className="absolute inset-0 z-30 pointer-events-none">
              <DotLottieReact src={confettiLottie} autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}

          {/* Vignette / spotlight during suspense — darkens edges to focus on reels */}
          <AnimatePresence>
            {phase === "suspense" && (
              <motion.div
                className="absolute inset-0 z-20 pointer-events-none rounded-2xl"
                style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="relative flex items-start justify-between gap-3 z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-[#FFD700]" />
                <span className="text-[11px] font-bold text-white uppercase tracking-[0.18em]">
                  {freeSpinOnly ? "Free Spin Slot" : "Deal Reward Slot"}
                </span>
              </div>
              <h4 className="text-white font-black text-lg leading-tight">
                {phase === "suspense" ? "..." :
                 phase === "reveal" && isWin ? rewardTitle(reward, { hideBonusAmount: isBonusAwaitingWheel, bonusCashAmount: visibleBonusCashAmount }) :
                 rewardTitle(phase === "reveal" || phase === "done" ? reward : null, { hideBonusAmount: isBonusAwaitingWheel, bonusCashAmount: visibleBonusCashAmount })}
              </h4>
              <p className="text-[#9A9ABF] text-xs sm:text-sm mt-1">
                {phase === "suspense" ? "" :
                 rewardSubtitle(phase === "reveal" || phase === "done" ? reward : null, recipientLabel, {
                   hideBonusAmount: isBonusAwaitingWheel,
                   freeSpinOnly,
                 })}
              </p>
              {!freeSpinOnly && (
                <p className="text-[#6A6A8A] text-xs mt-2">
                  Deal: <span className="font-semibold text-[#C9C9E8]">{displayMemberId}</span>
                </p>
              )}
            </div>

            <div
              className="shrink-0 w-11 h-11 rounded-xl border-2 flex items-center justify-center"
              style={{ borderColor: `${accent}66`, background: `${accent}1A` }}
            >
              {reward?.type === "points" && (phase === "reveal" || phase === "done") ? (
                <Target className="w-5 h-5" style={{ color: accent }} />
              ) : reward?.type === "bonus" && (phase === "reveal" || phase === "done") ? (
                <Gift className="w-5 h-5" style={{ color: accent }} />
              ) : reward?.type === "item" && (phase === "reveal" || phase === "done") ? (
                <Package className="w-5 h-5" style={{ color: accent }} />
              ) : reward?.type === "free_spins" && (phase === "reveal" || phase === "done") ? (
                <RefreshCcw className="w-5 h-5" style={{ color: accent }} />
              ) : reward?.type === "side_quest" && (phase === "reveal" || phase === "done") ? (
                <Swords className="w-5 h-5" style={{ color: accent }} />
              ) : reward?.type === "nothing" && (phase === "reveal" || phase === "done") ? (
                <X className="w-5 h-5" style={{ color: accent }} />
              ) : (
                <Coins className="w-5 h-5 text-[#FFD700]" />
              )}
            </div>
          </div>

          {/* ── Slot machine housing ─────────────────────────────────── */}
          <div
            className="mt-4 rounded-2xl p-4 relative overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #13132a 0%, #0B0B1A 100%)",
              boxShadow: "inset 0 2px 8px rgba(0,0,0,0.5), inset 0 -1px 4px rgba(255,255,255,0.03), 0 0 0 2px #2A2A45",
            }}
          >
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#4A4A65] to-transparent" />

            {/* Chase lights */}
            <div className="flex items-center justify-center gap-[6px] mb-4">
              {Array.from({ length: 15 }, (_, i) => {
                const color = i % 3 === 0 ? "#FFD700" : i % 3 === 1 ? "#F37020" : "#00CED1"
                const isActive = phase === "spinning" || phase === "suspense" || phase === "reveal"
                return (
                  <motion.span
                    key={i}
                    className="block w-[9px] h-[9px] rounded-full"
                    style={{
                      background: `radial-gradient(circle at 35% 35%, ${color}, ${color}99)`,
                      boxShadow: isActive
                        ? `0 0 8px 3px ${color}88, 0 0 14px 5px ${color}33`
                        : `0 0 4px 1px ${color}22`,
                    }}
                    animate={{
                      opacity: isActive ? [0.15, 1, 0.15] : [0.3, 0.7, 0.3],
                      scale: isActive ? [0.85, 1.35, 0.85] : [0.95, 1.05, 0.95],
                    }}
                    transition={{
                      duration: phase === "suspense" ? 0.6 : isActive ? 0.18 : 1.2,
                      repeat: Infinity,
                      delay: i * (isActive ? 0.03 : 0.06),
                    }}
                  />
                )
              })}
            </div>

            {/* Reel frame */}
            <div
              className="relative rounded-2xl p-3 pr-9"
              style={{
                background: "linear-gradient(180deg, #0E0E20, #08081A)",
                boxShadow: "inset 0 2px 10px rgba(0,0,0,0.6), inset 0 -1px 6px rgba(255,255,255,0.02), 0 0 0 1px #1E1E3A",
              }}
            >
              {/* Side chrome tabs */}
              <div
                className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-[10px] h-12 rounded-l-lg"
                style={{ background: "linear-gradient(180deg, #4A4A65, #2A2A45, #3A3A55)" }}
              />
              <div
                className="absolute -right-[6px] top-1/2 -translate-y-1/2 w-[10px] h-12 rounded-r-lg"
                style={{ background: "linear-gradient(180deg, #4A4A65, #2A2A45, #3A3A55)" }}
              />

              {/* Lever */}
              <div className="absolute right-[6px] top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-10">
                <div
                  className="w-[10px] h-14 rounded-full relative overflow-hidden"
                  style={{
                    background: "linear-gradient(180deg, #3A3A55, #2A2A45)",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
                  }}
                >
                  <motion.div
                    className="absolute left-1/2 -translate-x-1/2 w-[5px] h-9 rounded-full"
                    style={{
                      top: 3,
                      background: "linear-gradient(180deg, #e2e8f0, #94a3b8, #64748b)",
                      boxShadow: "0 0 4px rgba(255,255,255,0.15)",
                    }}
                    animate={leverPulled ? { y: [0, 12, 7] } : { y: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                </div>
                <motion.div
                  className="w-6 h-6 rounded-full border-2 border-white/15"
                  style={{
                    background: "radial-gradient(circle at 30% 30%, #ffb703, #f37020 60%, #c2410c)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.5), 0 0 8px rgba(243,112,32,0.3)",
                  }}
                  animate={leverPulled ? { y: [0, 12, 0], scale: [1, 0.92, 1] } : { y: 0, scale: 1 }}
                  transition={{ duration: 0.38, ease: "easeOut" }}
                />
              </div>

              {/* Reels — with spotlight dimming */}
              <div className="flex gap-2">
                <EmojiReel
                  spinning={phase === "spinning" || phase === "suspense"}
                  finalSymbol={reelValues[0]!}
                  delayMs={REEL_STAGGER[0]!}
                  onStop={() => handleReelStop(0)}
                  spotlight={spotlightReel(0)}
                />
                <EmojiReel
                  spinning={phase === "spinning" || phase === "suspense"}
                  finalSymbol={reelValues[1]!}
                  delayMs={REEL_STAGGER[1]!}
                  onStop={() => handleReelStop(1)}
                  spotlight={spotlightReel(1)}
                />
                <EmojiReel
                  spinning={phase === "spinning" || phase === "suspense"}
                  finalSymbol={reelValues[2]!}
                  delayMs={REEL_STAGGER[2]!}
                  onStop={() => handleReelStop(2)}
                  spotlight={spotlightReel(2)}
                />
              </div>

              {/* Winning line — gold glow that pulses on win */}
              <motion.div
                className="pointer-events-none absolute inset-x-3 top-1/2 -translate-y-1/2 h-[54px] rounded-lg"
                animate={
                  showWinGlow && isWin
                    ? {
                        borderColor: ["rgba(255,215,0,0.25)", "rgba(255,215,0,0.7)", "rgba(255,215,0,0.25)"],
                        boxShadow: [
                          "0 0 8px rgba(255,215,0,0.08), inset 0 0 8px rgba(255,215,0,0.05)",
                          "0 0 22px rgba(255,215,0,0.35), 0 0 44px rgba(255,215,0,0.15), inset 0 0 18px rgba(255,215,0,0.1)",
                          "0 0 8px rgba(255,215,0,0.08), inset 0 0 8px rgba(255,215,0,0.05)",
                        ],
                      }
                    : {
                        borderColor: "rgba(255,215,0,0.12)",
                        boxShadow: "0 0 12px rgba(255,215,0,0.06)",
                      }
                }
                transition={
                  showWinGlow && isWin
                    ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
                    : { duration: 0.3 }
                }
                style={{ border: "1px solid rgba(255,215,0,0.12)" }}
              />
            </div>

            {/* Bottom chase lights */}
            <div className="flex items-center justify-center gap-[6px] mt-4">
              {Array.from({ length: 15 }, (_, i) => {
                const color = i % 3 === 0 ? "#FFD700" : i % 3 === 1 ? "#F37020" : "#00CED1"
                const isActive = phase === "spinning" || phase === "suspense" || phase === "reveal"
                return (
                  <motion.span
                    key={`b-${i}`}
                    className="block w-[9px] h-[9px] rounded-full"
                    style={{
                      background: `radial-gradient(circle at 35% 35%, ${color}, ${color}99)`,
                      boxShadow: isActive
                        ? `0 0 8px 3px ${color}88, 0 0 14px 5px ${color}33`
                        : `0 0 4px 1px ${color}22`,
                    }}
                    animate={{
                      opacity: isActive ? [0.15, 1, 0.15] : [0.3, 0.7, 0.3],
                      scale: isActive ? [0.85, 1.35, 0.85] : [0.95, 1.05, 0.95],
                    }}
                    transition={{
                      duration: phase === "suspense" ? 0.6 : isActive ? 0.18 : 1.2,
                      repeat: Infinity,
                      delay: (15 - i) * (isActive ? 0.03 : 0.06),
                    }}
                  />
                )
              })}
            </div>

            {/* Status text — suspense phase shows pulsing dots */}
            <div
              className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-[#6A6A8A]"
              style={{ fontFamily: "Fredoka, sans-serif" }}
            >
              {phase === "ready" && (freeSpinOnly ? "Use your free spin to reveal your reward" : "Press Spin to reveal your reward")}
              {phase === "claiming" && "Locking your result..."}
              {phase === "spinning" && (
                <span>
                  Spinning reels...
                  {reelsLanded > 0 && <span className="text-[#FFD700] ml-1">{reelsLanded}/3 locked</span>}
                </span>
              )}
              {phase === "suspense" && (
                <motion.span
                  className="text-[#FFD700]"
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                >
                  Revealing result...
                </motion.span>
              )}
              {phase === "reveal" && isWin && (
                <motion.span
                  className="text-[#FFD700] text-sm font-black"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [1.3, 1], opacity: 1 }}
                  transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  {reward?.type === "points" && `+${countedValue} POINTS`}
                  {reward?.type === "bonus" && `$${countedValue} BONUS`}
                  {reward?.type === "item" && (reward.label || "MERCH WIN")}
                  {reward?.type === "free_spins" && `${reward.amount} FREE SPIN${reward.amount === 1 ? "" : "S"}`}
                  {reward?.type === "side_quest" && "SIDE QUEST!"}
                </motion.span>
              )}
              {phase === "reveal" && !isWin && <span>No reward this spin</span>}
              {phase === "miss" && (
                <motion.span
                  className="text-red-400 font-bold"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  No match! {spinAttempt < MAX_SPIN_ATTEMPTS
                    ? `Spin again (${spinAttempt}/${MAX_SPIN_ATTEMPTS})`
                    : ""}
                  {spinAttempt === MAX_SPIN_ATTEMPTS - 1 && (
                    <span className="text-[#FFD700] ml-1">— Next spin guaranteed!</span>
                  )}
                </motion.span>
              )}
              {phase === "done" && "Reward settled"}
              {phase === "error" && "Spin failed"}
            </div>

            {reward && (phase === "reveal" || phase === "done") && reward.label && !(reward.type === "bonus" && isBonusAwaitingWheel) && (
              <motion.div
                className="mt-3 flex justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold"
                  style={{ borderColor: `${accent}55`, background: `${accent}14`, color: accent }}
                >
                  <Sparkles className="w-3 h-3" />
                  <span>{reward.label}</span>
                </div>
              </motion.div>
            )}

            <div className="absolute bottom-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#3A3A55] to-transparent" />
          </div>

          {/* Prize tier info */}
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px]">
            {[
              { label: "POINTS", sub: "+10 to +100", color: "#32CD32" },
              { label: "BONUS", sub: "$1 to $10", color: "#00CED1" },
              { label: "MERCH", sub: "Shirt • Hat • Bottle", color: "#F37020" },
              { label: "FREE SPINS", sub: "1 or 2 spins", color: "#9B59B6" },
              { label: "QUEST", sub: "$5 to $50 cash", color: "#FF6B9D" },
              { label: "MISS", sub: "No extra reward", color: "#A0A0C0" },
            ].map((tier) => (
              <div
                key={tier.label}
                className="rounded-xl bg-[#0E0E20] p-2 text-center"
                style={{ boxShadow: "inset 0 1px 4px rgba(0,0,0,0.3), 0 0 0 1px #1E1E3A" }}
              >
                <p className="font-black" style={{ color: tier.color }}>{tier.label}</p>
                <p className="text-[#9A9ABF] mt-0.5">{tier.sub}</p>
              </div>
            ))}
          </div>

          {/* Bonus round — randomly wheel or super slots */}
          {phase === "done" && reward?.type === "bonus" && bonusMode === "wheel" && (
            <BonusRoundWheel amount={bonusRoundRevealAmount} onComplete={handleBonusWheelComplete} />
          )}
          {phase === "done" && reward?.type === "bonus" && bonusMode === "super_slots" && (
            <BonusSuperSlots amount={bonusRoundRevealAmount} onComplete={handleBonusWheelComplete} />
          )}
        </div>
      </ShakeContainer>

      {/* Action buttons */}
      <AnimatePresence mode="wait">
        {phase === "ready" && (
          <motion.button
            key="spin-button"
            type="button"
            onClick={freeSpinOnly ? handleFreeSpin : handleSpin}
            disabled={freeSpinOnly && pendingFreeSpins <= 0}
            className={`w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl font-black text-white ${
              freeSpinOnly && pendingFreeSpins <= 0 ? "cursor-not-allowed opacity-70" : "cursor-pointer"
            }`}
            style={{
              background: freeSpinOnly
                ? "linear-gradient(135deg, #9B59B6, #8E44AD)"
                : "linear-gradient(135deg, #FFD700, #F37020)",
              fontFamily: "Bangers, cursive",
              letterSpacing: "0.06em",
              fontSize: "1.1rem",
              boxShadow: freeSpinOnly
                ? "0 4px 20px rgba(155,89,182,0.3), inset 0 1px 0 rgba(255,255,255,0.15)"
                : "0 4px 20px rgba(243,112,32,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            whileHover={freeSpinOnly && pendingFreeSpins <= 0 ? undefined : {
              scale: 1.02,
              boxShadow: freeSpinOnly
                ? "0 8px 30px rgba(155,89,182,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
                : "0 8px 30px rgba(243,112,32,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
            whileTap={freeSpinOnly && pendingFreeSpins <= 0 ? undefined : { scale: 0.98 }}
          >
            <span className="inline-flex items-center gap-2">
              {freeSpinOnly ? <RefreshCcw className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
              {freeSpinOnly ? "Use Free Spin" : "Pull Slot Lever"}
            </span>
            <span className="inline-flex items-center gap-2 rounded-lg border border-black/15 bg-black/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
              <motion.span
                className="block w-2 h-2 rounded-full bg-white"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              {freeSpinOnly
                ? `${pendingFreeSpins} Free ${pendingFreeSpins === 1 ? "Spin" : "Spins"}`
                : "1 Spin"}
            </span>
          </motion.button>
        )}

        {(phase === "claiming" || phase === "spinning" || phase === "suspense" || phase === "reveal") && (
          <motion.div
            key="spinning"
            className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-xl text-white font-bold"
            style={{
              background: "linear-gradient(180deg, #151530, #0E0E22)",
              boxShadow: "0 0 0 2px #2A2A45, inset 0 1px 4px rgba(0,0,0,0.3)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {phase === "reveal" && isWin ? (
              <motion.div
                className="flex items-center gap-2"
                initial={{ scale: 0.8 }}
                animate={{ scale: [1.1, 1] }}
                transition={{ duration: 0.3 }}
              >
                <Sparkles className="w-5 h-5 text-[#FFD700]" />
                <span className="text-[#FFD700]" style={{ fontFamily: "Bangers, cursive", letterSpacing: "0.05em" }}>
                  Winner!
                </span>
              </motion.div>
            ) : (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-[#FFD700]" />
                <span style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {phase === "claiming" && "Securing your outcome..."}
                  {phase === "spinning" && "Reels spinning..."}
                  {phase === "suspense" && "Almost there..."}
                  {phase === "reveal" && "No bonus this time"}
                </span>
              </>
            )}
          </motion.div>
        )}

        {phase === "miss" && (
          <motion.button
            key="spin-again-button"
            type="button"
            onClick={handleSpinAgain}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl font-black text-white cursor-pointer"
            style={{
              background: spinAttempt >= MAX_SPIN_ATTEMPTS - 1
                ? "linear-gradient(135deg, #32CD32, #28a745)"
                : "linear-gradient(135deg, #FFD700, #F37020)",
              fontFamily: "Bangers, cursive",
              letterSpacing: "0.06em",
              fontSize: "1.1rem",
              boxShadow: spinAttempt >= MAX_SPIN_ATTEMPTS - 1
                ? "0 4px 20px rgba(50,205,50,0.4), inset 0 1px 0 rgba(255,255,255,0.15)"
                : "0 4px 20px rgba(243,112,32,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              {spinAttempt >= MAX_SPIN_ATTEMPTS - 1 ? "Last Chance — Guaranteed!" : "Spin Again"}
            </span>
            <span className="inline-flex items-center gap-2 rounded-lg border border-black/15 bg-black/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
              <motion.span
                className="block w-2 h-2 rounded-full"
                style={{ background: spinAttempt >= MAX_SPIN_ATTEMPTS - 1 ? "#32CD32" : "#fff" }}
                animate={{ opacity: [0.4, 1, 0.4], scale: spinAttempt >= MAX_SPIN_ATTEMPTS - 1 ? [1, 1.3, 1] : [1, 1, 1] }}
                transition={{ duration: 0.6, repeat: Infinity }}
              />
              {spinAttempt}/{MAX_SPIN_ATTEMPTS}
            </span>
          </motion.button>
        )}

        {(phase === "done" || phase === "error") && (
          <motion.div
            key="done-actions"
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {errorMessage && (
              <div className="rounded-xl border-2 border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {errorMessage}
              </div>
            )}
            {/* Free spin button — appears prominently when user has pending free spins */}
            {phase === "done" && pendingFreeSpins > 0 && !isBonusAwaitingWheel && (
              <button
                type="button"
                onClick={handleFreeSpin}
                className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl font-black text-white cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #9B59B6, #8E44AD)",
                  fontFamily: "Bangers, cursive",
                  letterSpacing: "0.06em",
                  fontSize: "1.1rem",
                  boxShadow: "0 4px 20px rgba(155,89,182,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <RefreshCcw className="w-5 h-5" />
                  Use Free Spin
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg border border-black/15 bg-black/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
                  <motion.span
                    className="block w-2 h-2 rounded-full bg-white"
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                  {pendingFreeSpins} Free {pendingFreeSpins === 1 ? "Spin" : "Spins"}
                </span>
              </button>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {phase === "error" ? (
                <button
                  type="button"
                  onClick={isFreeSpin ? handleFreeSpin : handleSpin}
                  className="px-4 py-3 rounded-xl border-2 border-[#2A2A45] bg-[#FFD700] text-[#121220] font-black cursor-pointer"
                >
                  Retry Spin
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onSubmitAnother}
                  disabled={isBonusAwaitingWheel || pendingFreeSpins > 0}
                  className={`px-4 py-3 rounded-xl border-2 border-[#2A2A45] text-white font-black ${
                    isBonusAwaitingWheel || pendingFreeSpins > 0 ? "bg-[#222240] text-[#6A6A8A] cursor-not-allowed" : "bg-[#32CD32] cursor-pointer"
                  }`}
                >
                  {isBonusAwaitingWheel
                    ? "Finish Bonus Round First"
                    : pendingFreeSpins > 0
                      ? "Use Free Spins First"
                      : freeSpinOnly
                        ? "Back to Dashboard"
                        : "Submit Another Deal"}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-3 rounded-xl border-2 border-[#2A2A45] bg-[#1C1C32] text-white font-bold cursor-pointer"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
